import { JSX, ReactNode } from 'react';
import {
    Datagrid,
    DateField,
    Edit,
    EditButton,
    NumberInput,
    Pagination,
    ReferenceManyField,
    SelectField,
    SelectInput,
    TabbedForm,
    TextField,
    TextInput,
    required,
    useRecordContext
} from 'react-admin';
import { Box, Chip, Grid, LinearProgress, Typography } from '@mui/material';
import { useSocialList, useYearChoices } from "../../defaults/defaults";
import { useGetIndustries } from "../global-hooks/default-system-hooks";
import { ChatFieldBridge } from "../chat-bot/components/ChatFieldBridge";
import { CompanyCard } from "./components/Company/CompanyCard";
import { CompanySummaryStrip } from "./components/Company/CompanySummaryStrip";
import { CompanyDomainsTab } from "./components/Company/CompanyDomainsTab";

/**
 * A company, on one screen.
 *
 * **The Show screen was merged into this one on 2026-09-10, at the user's instruction, and the
 * `/companies/:id/show` route is gone.** They had grown apart: Show rendered eight nested
 * `<Show>` cards over the connected records and none of the company's own detail beyond its name
 * and number, while Edit was a six-field `SimpleForm` that could see none of the connected records
 * at all. So the screen that could tell you what a company had could not change it, the screen
 * that could change it could not tell you, and seven columns the API has always answered with
 * were on neither: trading name, employee count, years in business, logo, privacy policy URL and
 * the two campaign collections. `adminCompanyDataProvider` dropped those on the floor, so they are
 * mapped now as well.
 *
 * Tabs rather than one long page, matching the customer account screen next door. The Details tab
 * carries the fields; every other tab is one kind of connected record, with its count in the card
 * corner so an empty list is tellable from an unloaded one.
 */
const CompanyTitle: () => JSX.Element|null = (): JSX.Element|null => {
    const company: any = useRecordContext();
    if (company === undefined || company === null) {
        return null;
    }

    return <span>{ 'Company - ' + (company.name ?? company.id) }</span>;
}

/**
 * A card whose count comes from the company's own record, so it is there before the list
 * underneath has loaded.
 */
const RelatedCard: (props: { title: string, source: string, note?: string, children: ReactNode }) => JSX.Element =
    (props: { title: string, source: string, note?: string, children: ReactNode }): JSX.Element => {
        const company: any = useRecordContext();

        return (
            <CompanyCard title={ props.title }
                         count={ company?.[props.source]?.length ?? 0 }
                         note={ props.note }>
                { props.children }
            </CompanyCard>
        );
    }

/**
 * The company's own email domain and whether it has been proved, read only.
 *
 * Neither field was on either old screen. They are shown rather than offered as inputs because
 * `verifiedDomainEmail` is a record of something that happened, the same way a domain's status is,
 * and an admin ticking a box is not that thing happening.
 */
const DomainEmailReading: () => JSX.Element = (): JSX.Element => {
    const company: any = useRecordContext();
    const email: string|undefined = company?.domainEmail;

    return (
        <Box sx={ { display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' } }>
            <Typography sx={ {
                fontSize: 'var(--wf-eyebrow, 9.5px)',
                letterSpacing: 'var(--wf-track, 0.16em)',
                textTransform: 'uppercase',
                fontWeight: 700,
                color: 'text.secondary',
            } }>
                { 'Email domain' }
            </Typography>
            <Typography sx={ { fontFamily: 'var(--wf-font-mono, monospace)', fontSize: '14px' } }>
                { email === undefined || email === null || email === '' ? 'None on record' : email }
            </Typography>
            { (email !== undefined && email !== null && email !== '') &&
                <Chip label={ company?.verifiedDomainEmail === true ? 'Verified' : 'Unverified' }
                      size="small"
                      variant="outlined"
                      sx={ {
                          fontWeight: 600,
                          color: company?.verifiedDomainEmail === true
                              ? 'var(--wf-ok, #15803d)'
                              : 'var(--wf-ink-faint, #74909b)',
                          borderColor: company?.verifiedDomainEmail === true
                              ? 'var(--wf-ok, #15803d)'
                              : 'var(--wf-ink-faint, #74909b)',
                      } } />
            }
        </Box>
    );
}

export const AdminCompanyEdit: () => JSX.Element = (): JSX.Element => {
    const yearChoices: { id: string, name: string }[] = useYearChoices();
    const socialList: { id: string, name: string }[] = useSocialList();
    const { loadedIndustries, loadingIndustries } = useGetIndustries();

    return (
        <Edit title={ <CompanyTitle /> } redirect={ false }>
            <TabbedForm>
                <TabbedForm.Tab label="Details">
                    <CompanySummaryStrip />
                    <Grid container spacing={ 2 } sx={ { width: '100%' } }>
                        <Grid size={ { xs: 12, md: 6 } } sx={ { display: 'flex', flexDirection: 'column' } }>
                            <CompanyCard title="Identity"
                                         note="What the company is called and how it is registered.">
                                <TextInput name="name" source="name" validate={ [required()] } fullWidth />
                                <TextInput name="tradingName"
                                           source="tradingName"
                                           label="Trading name"
                                           helperText="Only if it differs from the registered name."
                                           fullWidth />
                                <TextInput name="companyNumber" source="companyNumber" fullWidth />
                                <TextInput name="vat" source="vat" label="VAT number" fullWidth />
                                <TextInput name="sicCode" source="sicCode" label="SIC code" fullWidth />
                                { (loadingIndustries) &&
                                    <LinearProgress sx={ { margin: '10px 0', width: '50%' } } />
                                }
                                { (!loadingIndustries) &&
                                    <SelectInput
                                        name="industry"
                                        source="industry"
                                        choices={ loadedIndustries }
                                        optionValue="id"
                                        fullWidth
                                    />
                                }
                            </CompanyCard>
                        </Grid>
                        <Grid size={ { xs: 12, md: 6 } } sx={ { display: 'flex', flexDirection: 'column', gap: '16px' } }>
                            <CompanyCard title="Size and history"
                                         note="Answered by the customer when they set the company up, and used to prefill campaign data points.">
                                <SelectInput
                                    name="yearEstablished"
                                    source="yearEstablished"
                                    label="Year established"
                                    choices={ yearChoices }
                                    optionValue="id"
                                    fullWidth
                                />
                                <TextInput name="yearsInBusiness"
                                           source="yearsInBusiness"
                                           label="Years in business"
                                           fullWidth />
                                <NumberInput name="numberOfEmployees"
                                             source="numberOfEmployees"
                                             label="Number of employees"
                                             helperText="What the customer told us, which is not the People tab's count."
                                             fullWidth />
                            </CompanyCard>
                            <CompanyCard title="Policies and email domain"
                                         note="Remembered so a campaign asking for a privacy policy prefills instead of asking again.">
                                <TextInput name="privacyPolicyUrl"
                                           source="privacyPolicyUrl"
                                           label="Privacy policy URL"
                                           fullWidth />
                                <DomainEmailReading />
                            </CompanyCard>
                        </Grid>
                    </Grid>
                    {/* Offers this form to the assistant. Renders nothing, writes nothing by
                        itself, and lives inside a tab because TabbedFormTabs clones every direct
                        child of the form into the tab bar. */}
                    <ChatFieldBridge />
                </TabbedForm.Tab>

                <TabbedForm.Tab label="Domains">
                    <CompanyDomainsTab />
                </TabbedForm.Tab>

                <TabbedForm.Tab label="People">
                    <RelatedCard title="Employees"
                                 source="employees"
                                 note="The contacts a campaign can be sent to.">
                        <ReferenceManyField
                            label={ false }
                            source="employees"
                            reference="employees"
                            target="id"
                            pagination={ <Pagination /> }
                        >
                            <Datagrid bulkActionButtons={ false } sx={ { width: '100%' } }>
                                <TextField source="id" />
                                <TextField source="name" />
                                <TextField source="position" label="Position" />
                                <TextField source="email" />
                                <TextField source="status" sx={ { textTransform: 'capitalize' } } />
                                <EditButton />
                            </Datagrid>
                        </ReferenceManyField>
                    </RelatedCard>
                </TabbedForm.Tab>

                <TabbedForm.Tab label="Contact">
                    <Grid container spacing={ 2 } sx={ { width: '100%' } }>
                        <Grid size={ { xs: 12, lg: 6 } }>
                            <RelatedCard title="Addresses" source="addresses">
                                <ReferenceManyField
                                    label={ false }
                                    source="addresses"
                                    reference="addresses"
                                    target="id"
                                    pagination={ <Pagination /> }
                                >
                                    <Datagrid bulkActionButtons={ false } sx={ { width: '100%' } }>
                                        <TextField source="line_one" label="Address" />
                                        <TextField source="city" />
                                        <TextField source="postcode" />
                                        <EditButton />
                                    </Datagrid>
                                </ReferenceManyField>
                            </RelatedCard>
                        </Grid>
                        <Grid size={ { xs: 12, lg: 6 } }>
                            <RelatedCard title="Email addresses" source="emails">
                                <ReferenceManyField
                                    label={ false }
                                    source="emails"
                                    reference="emails"
                                    target="id"
                                    pagination={ <Pagination /> }
                                >
                                    <Datagrid bulkActionButtons={ false } sx={ { width: '100%' } }>
                                        <TextField source="email" />
                                        <TextField source="description" />
                                        <EditButton />
                                    </Datagrid>
                                </ReferenceManyField>
                            </RelatedCard>
                        </Grid>
                        <Grid size={ { xs: 12, lg: 6 } }>
                            <RelatedCard title="Telephone numbers" source="telephones">
                                <ReferenceManyField
                                    label={ false }
                                    source="telephones"
                                    reference="telephones"
                                    target="id"
                                    pagination={ <Pagination /> }
                                >
                                    <Datagrid bulkActionButtons={ false } sx={ { width: '100%' } }>
                                        <TextField source="telephone" />
                                        <TextField source="description" />
                                        <EditButton />
                                    </Datagrid>
                                </ReferenceManyField>
                            </RelatedCard>
                        </Grid>
                        <Grid size={ { xs: 12, lg: 6 } }>
                            <RelatedCard title="Social media" source="socials">
                                <ReferenceManyField
                                    label={ false }
                                    source="socials"
                                    reference="social_media"
                                    target="id"
                                    pagination={ <Pagination /> }
                                >
                                    <Datagrid bulkActionButtons={ false } sx={ { width: '100%' } }>
                                        <SelectField source="platform" choices={ socialList } />
                                        <TextField source="url" label="Profile" />
                                        <EditButton />
                                    </Datagrid>
                                </ReferenceManyField>
                            </RelatedCard>
                        </Grid>
                    </Grid>
                </TabbedForm.Tab>

                <TabbedForm.Tab label="Campaigns">
                    <Grid container spacing={ 2 } sx={ { width: '100%' } }>
                        <Grid size={ { xs: 12, lg: 6 } }>
                            <RelatedCard title="Mystery Shopper"
                                         source="mysteryShoppers"
                                         note="Campaigns run against this company's contacts.">
                                <ReferenceManyField
                                    label={ false }
                                    source="mysteryShoppers"
                                    reference="mystery_shoppers"
                                    target="id"
                                    pagination={ <Pagination /> }
                                >
                                    <Datagrid bulkActionButtons={ false } sx={ { width: '100%' } }>
                                        <TextField source="id" />
                                        <TextField source="name" />
                                        <TextField source="status" sx={ { textTransform: 'capitalize' } } />
                                        <DateField source="createdAt" label="Created" />
                                        <EditButton />
                                    </Datagrid>
                                </ReferenceManyField>
                            </RelatedCard>
                        </Grid>
                        <Grid size={ { xs: 12, lg: 6 } }>
                            <RelatedCard title="Compliance Sniffer"
                                         source="complianceSniffers"
                                         note="Campaigns run against this company's own website.">
                                <ReferenceManyField
                                    label={ false }
                                    source="complianceSniffers"
                                    reference="compliance_sniffers"
                                    target="id"
                                    pagination={ <Pagination /> }
                                >
                                    <Datagrid bulkActionButtons={ false } sx={ { width: '100%' } }>
                                        <TextField source="id" />
                                        <TextField source="name" />
                                        <TextField source="status" sx={ { textTransform: 'capitalize' } } />
                                        <DateField source="createdAt" label="Created" />
                                        <EditButton />
                                    </Datagrid>
                                </ReferenceManyField>
                            </RelatedCard>
                        </Grid>
                    </Grid>
                </TabbedForm.Tab>
            </TabbedForm>
        </Edit>
    );
}
